// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import Authentication from '@app/features/auth/state/Authentication';
import type {GatewayHandlerContext} from '@app/features/gateway/events/EventRouter';
import KonvoNotificationPrefs from '@app/features/konvo_notifications/state/KonvoNotificationPrefs';
import * as NotificationUtils from '@app/features/notification/utils/NotificationUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import LocalPresence from '@app/features/presence/state/LocalPresence';
import StreamerMode from '@app/features/streamer_mode/state/StreamerMode';
import NotificationState from '@app/features/ui/state/Notification';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import {StatusTypes} from '@konvo/constants/src/StatusConstants';
import {MailNotificationSchema} from '@konvo/schema/src/domains/konvo/MailNotification';

const logger = new Logger('MailNotification');
const seen = new Set<string>();
const MAX_REMEMBERED = 256;

/** No visibility gate or poll: incoming mail can notify a minimized desktop window. */
export async function handleKonvoMailNotification(data: unknown, _context: GatewayHandlerContext): Promise<void> {
	// Web/mobile already receive the server's push; the Rail must not duplicate the main window.
	if (!getElectronAPI() || window.location.pathname === Routes.RAIL) return;
	const parsed = MailNotificationSchema.safeParse(data);
	if (!parsed.success) return;
	const userId = Authentication.currentUserId;
	if (!userId) return;
	await KonvoNotificationPrefs.ensureLoaded();
	if (
		Authentication.currentUserId !== userId ||
		!NotificationState.browserNotificationsEnabled ||
		!KonvoNotificationPrefs.allowsChannel('mail', 'desktop') ||
		StreamerMode.shouldDisableNotifications ||
		LocalPresence.getStatus() === StatusTypes.DND
	) {
		return;
	}
	const notification = parsed.data;
	const key = `${userId}:${notification.notification_id}`;
	if (seen.has(key)) return;
	seen.add(key);
	if (seen.size > MAX_REMEMBERED) seen.delete(seen.values().next().value!);
	try {
		const result = await NotificationUtils.showNotification({
			id: notification.tag,
			title: `Outlook · ${notification.title}`,
			body: notification.body,
			url: Routes.dmMail(notification.mail_message_id),
			playSound: KonvoNotificationPrefs.allowsChannel('mail', 'sound'),
		});
		// Permit a replay to retry failed native delivery. Claim before awaiting to dedupe concurrent events.
		if (!result.nativeNotificationId) seen.delete(key);
	} catch (error) {
		seen.delete(key);
		logger.error('Native mail notification failed', {error});
	}
}
